import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

interface SortableDiagnosisCardProps {
  id: string;
  variant: 'primary' | 'secondary';
  /** No drag handle and no drag response — for slots with nothing to trade places with. */
  disabled?: boolean;
  children: React.ReactNode;
}

const SortableDiagnosisCard: React.FC<SortableDiagnosisCardProps> = ({
  id,
  variant,
  disabled = false,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  // Translate only: this context mixes the full-width primary slot with the
  // secondary grid cells, and the strategy's scale ratios between rects of such
  // different sizes distort the card being previewed.
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  const isPrimary = variant === 'primary';

  return (
    <div ref={setNodeRef} style={style} className='relative group'>
      {/* Drag Handle — outside the framed card */}
      {!disabled && (
        <div
          {...attributes}
          {...listeners}
          className='absolute -left-3.5 top-1/2 -translate-y-1/2 cursor-grab active:cursor-grabbing z-10 p-1.5 hover:bg-primary-50 rounded-md transition-opacity opacity-0 group-hover:opacity-100'
          title={
            isPrimary
              ? 'Drag into secondary codes to demote'
              : 'Drag to reorder or promote'
          }
        >
          <GripVertical className='w-4 h-4 text-primary-600' />
        </div>
      )}

      {children}
    </div>
  );
};

export default SortableDiagnosisCard;
